/**
 * Quem está entrando, o que mostrar por causa disso, e em qual endereço.
 *
 * Ao abrir, o painel pergunta ao servidor quem é o dono do cookie. Se não há cookie, ou ele
 * venceu, aparece a tela de entrar. Não existe "está logado?" guardado no navegador: quem
 * responde isso é sempre o servidor, porque a resposta do navegador seria só um palpite.
 *
 * O endereço decide a tela (ver `rotas.ts`). Quem chega direto numa congregação sem ter
 * entrado vê a tela de entrar e, depois de entrar, continua onde queria ir: o endereço não se
 * perde, porque ele nunca foi trocado.
 */
import { useCallback, useEffect, useState, type JSX } from 'react'
import { servidor, type Eu } from './api'
import { Aguardando } from './paginas/Aguardando'
import { Auditoria } from './paginas/Auditoria'
import { Campo } from './paginas/Campo'
import { Congregacao } from './paginas/Congregacao'
import { SaidasDoMes } from './paginas/SaidasDoMes'
import { FechamentoDaCongregacao } from './paginas/FechamentoDaCongregacao'
import { Agenda } from './paginas/Agenda'
import { ConferenciaDoCartao } from './paginas/ConferenciaDoCartao'
import { Dizimistas } from './paginas/Dizimistas'
import { RepassesDaCongregacao, RepassesDoCampo } from './paginas/Repasses'
import { Entrar } from './paginas/Entrar'
import { Notebooks } from './paginas/Notebooks'
import { Pessoas } from './paginas/Pessoas'
import { Saidas } from './paginas/Saidas'
import { SegundoFator } from './paginas/SegundoFator'
import { MinhaSenha } from './paginas/MinhaSenha'
import { Sobre } from './paginas/Sobre'
import { TrocarASenha } from './paginas/TrocarASenha'
import { Administracao } from './paginas/Administracao'
import { Inicio } from './paginas/Inicio'
import { FaixaDoTreinamento } from './componentes/FaixaDoTreinamento'
import { FaixaDoAmbiente } from './componentes/FaixaDoAmbiente'
import { rotaDeAgora, type Rota } from './rotas'
import './estilos/papel.css'
import './estilos/painel.css'

type Estado = { fase: 'perguntando' } | { fase: 'fora' } | { fase: 'dentro'; eu: Eu }

/**
 * A FAIXA DO AMBIENTE vem acima de TUDO, inclusive das telas sem sessão. Ver `FaixaDoAmbiente`.
 */
export function App(): JSX.Element {
  return (
    <>
      <FaixaDoAmbiente />
      <AsTelas />
    </>
  )
}

function AsTelas(): JSX.Element {
  const [estado, setEstado] = useState<Estado>({ fase: 'perguntando' })
  const [rota, setRota] = useState<Rota>(() => rotaDeAgora())

  useEffect(() => {
    servidor
      .eu()
      .then((quem) => setEstado(quem.dentro ? { fase: 'dentro', eu: quem } : { fase: 'fora' }))
      // Servidor fora do ar ou internet caída: a tela de entrar é o lugar certo de esperar.
      .catch(() => setEstado({ fase: 'fora' }))
  }, [])

  // O botão de voltar do navegador, e o `irPara`, que dispara o mesmo evento.
  useEffect(() => {
    const aoAndar = (): void => setRota(rotaDeAgora())
    window.addEventListener('popstate', aoAndar)
    return () => window.removeEventListener('popstate', aoAndar)
  }, [])

  const sair = useCallback(() => setEstado({ fase: 'fora' }), [])

  /**
   * Voltar do treinamento para o Campo de verdade.
   *
   * Recarrega a pagina em vez de so mexer no estado: a sessao trocou de Campo do lado do
   * servidor, e TODA tela aberta esta com numeros do outro Campo na memoria. Tentar remendar
   * cada uma delas seria esquecer uma.
   */
  const voltarDoTreinamento = useCallback(() => {
    void servidor.sairDoTreinamento().then(() => window.location.reload())
  }, [])

  /*
   * A MARCA NO CORPO DA PAGINA, que e de onde sai a borda amarela da janela inteira.
   *
   * Ela mora aqui, e nao no componente da faixa, porque o `body` e de fora do React: um
   * componente que se desenha e se apaga nao deve mexer em coisa que nao e dele.
   */
  useEffect(() => {
    const modo = estado.fase === 'dentro' && estado.eu.treinamento ? 'treinamento' : ''
    if (modo) document.body.dataset['modo'] = modo
    else delete document.body.dataset['modo']
  }, [estado])

  /*
   * A página do link de trocar a senha vem ANTES de tudo, inclusive de esperar a resposta de
   * "quem sou eu". Ela não precisa de sessão, e quem chega nela não tem: esperar aqui seria
   * mostrar "Abrindo..." para quem clicou num link do e-mail, sem motivo nenhum.
   */
  if (rota.tela === 'trocar-senha') return <TrocarASenha token={rota.token} />

  /*
   * A tela de Sobre, pelo mesmo motivo e no mesmo lugar: ela responde "o servidor está no ar?",
   * e esperar pela resposta de "quem sou eu" para mostrar isso seria esperar exatamente pela
   * chamada que não vai voltar quando a resposta for "não está".
   */
  if (rota.tela === 'sobre') return <Sobre />

  /*
   * A ADMINISTRAÇÃO também vem antes, e pelo motivo mais forte dos três: ela tem sessão própria,
   * noutro cookie e noutra porta. Esperar a resposta de "quem sou eu" do PAINEL para abrir esta
   * tela mostraria a entrada do pastor a quem veio administrar a hospedagem, e a conta dele não
   * entra por aquela porta.
   */
  if (rota.tela === 'administracao') return <Administracao parte={rota.parte} />

  if (estado.fase === 'perguntando') return <p className="tela-centro">Abrindo...</p>
  if (estado.fase === 'fora') return <Entrar aoEntrar={(eu) => setEstado({ fase: 'dentro', eu })} />

  /*
   * A conta nova não vê nada antes de ligar o segundo fator.
   *
   * O servidor recusa de qualquer jeito, em toda rota: esta linha não é a trava, é a educação.
   * Sem ela a pessoa veria o Início pela metade, com um erro em cada caixa, e teria que
   * adivinhar que o caminho era outro.
   */
  if (estado.eu.deve_ligar_segundo_fator) {
    return <SegundoFator eu={estado.eu} aoSair={sair} obrigada />
  }

  /*
   * A FAIXA DO TREINAMENTO FICA FORA DAS ROTAS, e é isso que a faz valer.
   *
   * Posta aqui, acima de toda tela, ela aparece em **todas** sem ninguém precisar lembrar dela em
   * cada uma, e tela nova a herda do mesmo jeito que herda a barra de cima. É a única forma que
   * não depende de memória de gente.
   *
   * Por isso o resto virou uma função: a escolha da tela continua exatamente como estava, e o
   * que mudou foi só passar a desenhar a faixa junto.
   */
  return (
    <>
      <FaixaDoTreinamento eu={estado.eu} aoSair={voltarDoTreinamento} />
      {aTela(estado.eu, rota)}
    </>
  )

  /*
   * O `rota` vai por parametro, e com o tipo ja estreitado.
   *
   * As duas telas que nao precisam de sessao (o link da senha e o Sobre) sairam la em cima, por
   * `return`, mas o TypeScript nao leva esse estreitamento para dentro de uma funcao: de dentro
   * dela, `rota` voltaria a poder ser qualquer uma das tres. O `Exclude` diz o que ja se sabe.
   */
  function aTela(
    eu: Eu,
    rota: Exclude<Rota, { tela: 'trocar-senha' } | { tela: 'sobre' } | { tela: 'administracao' }>
  ): JSX.Element {
  if (rota.tela === 'congregacao') {
    return (
      <Congregacao
        eu={eu}
        id={rota.id}
        competenciaDoEndereco={rota.competencia}
        aoSair={sair}
      />
    )
  }

  if (rota.tela === 'fechamento-da-congregacao') {
    return (
      <FechamentoDaCongregacao
        eu={eu}
        id={rota.id}
        competenciaDoEndereco={rota.competencia}
        aoSair={sair}
      />
    )
  }

  if (rota.tela === 'saidas-da-congregacao') {
    return (
      <SaidasDoMes
        eu={eu}
        id={rota.id}
        competenciaDoEndereco={rota.competencia}
        aoSair={sair}
      />
    )
  }

  /* A fase 6 do espelho (19/10/2026). */
  if (rota.tela === 'dizimistas') {
    return <Dizimistas eu={eu} id={rota.id} competenciaDoEndereco={rota.competencia} aoSair={sair} />
  }
  if (rota.tela === 'repasses-da-congregacao') {
    return <RepassesDaCongregacao eu={eu} id={rota.id} competenciaDoEndereco={rota.competencia} aoSair={sair} />
  }
  if (rota.tela === 'agenda') {
    return <Agenda eu={eu} id={rota.id} competenciaDoEndereco={rota.competencia} aoSair={sair} />
  }
  if (rota.tela === 'repasses-do-campo') {
    return <RepassesDoCampo eu={eu} competenciaDoEndereco={rota.competencia} aoSair={sair} />
  }
  if (rota.tela === 'conferencia-do-cartao') {
    return <ConferenciaDoCartao eu={eu} competenciaDoEndereco={rota.competencia} aoSair={sair} />
  }

  if (rota.tela === 'campo') {
    return (
      <Campo
        eu={eu}
        competenciaDoEndereco={rota.competencia}
        versaoDoEndereco={rota.versao}
        aoSair={sair}
      />
    )
  }

  if (rota.tela === 'aguardando') return <Aguardando eu={eu} aoSair={sair} />
  if (rota.tela === 'saidas') return <Saidas eu={eu} aoSair={sair} />
  if (rota.tela === 'notebooks') return <Notebooks eu={eu} aoSair={sair} />
  if (rota.tela === 'pessoas') return <Pessoas eu={eu} aoSair={sair} />
  if (rota.tela === 'auditoria') return <Auditoria eu={eu} aoSair={sair} />
  if (rota.tela === 'segundo-fator') return <SegundoFator eu={eu} aoSair={sair} />
  if (rota.tela === 'minha-senha') return <MinhaSenha eu={eu} aoSair={sair} />

  return <Inicio eu={eu} diaDoEndereco={rota.dia} aoSair={sair} />
  }
}
